import type { TutorBillingTableColumn, TutorBillingTableRow } from './interfaces'

import { TutorBillingChangeRequestStatus } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorBillingFlag, TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingBulkFlags } from '../api/bulk-flags.hook'
import { useTutorBillingChangeRequestAction } from '../api/change-request.hook'
import { useTutorBillingErase } from '../api/erase.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import {
  buildTutorBillingFields,
  formatTutorBillingDate,
  getTutorBillingTutorName,
  joinTutorBillingMeta,
} from '../lib/tutor-billing-fields'

interface ModalTutorBillingNewAccountReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  subtitle: ComputedRef<string | null>
  columns: TutorBillingTableColumn[]
  rows: ComputedRef<TutorBillingTableRow[]>
  setClose: () => void
  setDiscard: () => Promise<void>
  setAddToSalesforce: () => Promise<void>
}

const COLUMNS: TutorBillingTableColumn[] = [
  { key: 'submitted', label: 'Submitted', tone: 'accent' },
]

export const useModalTutorBillingNewAccount = (): ModalTutorBillingNewAccountReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.NewAccount))
  const isSaving = computed<boolean>(() => store.isSaving)

  const subtitle = computed<string | null>(() => {
    const row = store.getActiveRow
    if (!row) return null

    return joinTutorBillingMeta([
      `Record ${row.id}`,
      getTutorBillingTutorName(row),
      row.submitted_at ? `submitted ${formatTutorBillingDate(row.submitted_at)}` : null,
    ])
  })

  const rows = computed<TutorBillingTableRow[]>(() => {
    const row = store.getActiveRow
    if (!row) return []

    return buildTutorBillingFields(row, listsStore.getTutorBillingPaymentServiceLabel).map(field => ({
      key: field.key,
      label: field.label,
      cells: [{ value: field.value, tone: 'accent' }],
    }))
  })

  const setCloseFor = (rowId: number): void => {
    if (store.isModalFor(TutorBillingModal.NewAccount, rowId)) store.closeModal()
  }

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setDiscard = async (): Promise<void> => {
    const rowId = store.getActiveRow?.id
    if (!rowId || store.isSaving) return

    const isErased = await useTutorBillingErase(rowId)
    if (!isErased) return

    store.deselect(rowId)
    setCloseFor(rowId)
    void useTutorBillingListApi()
  }

  const setAddToSalesforce = async (): Promise<void> => {
    const row = store.getActiveRow
    if (!row || store.isSaving) return

    const changeRequest = row.change_request
    const isAwaitingSalesforce = changeRequest?.status === TutorBillingChangeRequestStatus.AwaitingSalesforce

    const isDone = isAwaitingSalesforce
      ? Boolean(await useTutorBillingChangeRequestAction(changeRequest.id, 'confirm-salesforce'))
      : await useTutorBillingBulkFlags([row.id], { [TutorBillingFlag.SalesforceAccount]: true })

    if (!isDone) return

    setCloseFor(row.id)
    void useTutorBillingListApi()
  }

  return {
    isOpen,
    isSaving,
    subtitle,
    columns: COLUMNS,
    rows,
    setClose,
    setDiscard,
    setAddToSalesforce,
  }
}
